import React, { useState, useEffect } from 'react';

function Backups() {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => {
    fetchBackups();
  }, []);

  const fetchBackups = async () => {
    try {
      setLoading(true);
      const res = await fetch('/api/backups/list');
      const data = await res.json();
      setBackups(data.backups || []);
    } catch (err) {
      console.error('Error:', err);
    } finally {
      setLoading(false);
    }
  };

  const runBackup = async () => {
    setRunning(true);
    setMessage('');
    try {
      const res = await fetch('/api/backups/create', { method: 'POST' });
      const data = await res.json();
      if (data.success) {
        setMessage('✅ Backup created successfully!');
        fetchBackups();
      } else {
        setMessage('❌ Error: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('❌ Error: ' + err.message);
    } finally {
      setRunning(false);
    }
  };

  const testRestore = async (filename) => {
    setMessage('🧪 Testing restore...');
    try {
      const res = await fetch('/api/backups/test', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename })
      });
      const data = await res.json();
      if (data.success) {
        setMessage(`✅ Test passed! ${data.tables} tables, ${data.rows} total rows`);
      } else {
        setMessage('❌ Test failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('❌ Error: ' + err.message);
    }
  };

  const restoreBackup = async (filename) => {
    const confirm = window.confirm(`⚠️ RESTORE: This will overwrite ALL current data with backup: ${filename}\n\nType 'yes' in prompt to confirm.`);
    if (!confirm) return;
    
    const answer = window.prompt('Type "yes" to confirm restore:');
    if (answer !== 'yes') {
      setMessage('Restore cancelled');
      return;
    }
    
    setMessage('🔄 Restoring...');
    try {
      const res = await fetch('/api/backups/restore', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filename })
      });
      const data = await res.json();
      if (data.success) {
        setMessage('✅ Restore complete! NGX-MC will restart.');
      } else {
        setMessage('❌ Restore failed: ' + (data.error || 'Unknown error'));
      }
    } catch (err) {
      setMessage('❌ Error: ' + err.message);
    }
  };

  if (loading) return <div className="p-4">Loading...</div>;

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">💾 NGX-MC Backups</h1>
        <button 
          onClick={runBackup}
          disabled={running}
          className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
        >
          {running ? 'Running...' : '🆕 Create Backup'}
        </button>
      </div>

      {message && (
        <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded text-sm">
          {message}
        </div>
      )}

      <div className="bg-white rounded-lg shadow p-4 mb-6">
        <h2 className="font-semibold mb-2">Backup Info</h2>
        <p className="text-sm text-gray-600">
          Location: <code>/home/orion/backups/ngx-mc/</code><br/>
          Max backups kept: 10<br/>
          Database: <code>ngx-mc.db</code>
        </p>
      </div>

      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-100">
            <tr>
              <th className="px-4 py-3 text-left">Backup File</th>
              <th className="px-4 py-3 text-left">Size</th>
              <th className="px-4 py-3 text-left">Date</th>
              <th className="px-4 py-3 text-left">Actions</th>
            </tr>
          </thead>
          <tbody>
            {backups.length === 0 ? (
              <tr>
                <td colSpan="4" className="px-4 py-8 text-center text-gray-500">
                  No backups found
                </td>
              </tr>
            ) : (
              backups.map((backup) => (
                <tr key={backup.name} className="border-t hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs">{backup.name}</td>
                  <td className="px-4 py-3">{backup.size}</td>
                  <td className="px-4 py-3">{backup.date}</td>
                  <td className="px-4 py-3">
                    <button
                      onClick={() => testRestore(backup.name)}
                      className="px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200 mr-2"
                    >
                      🧪 Test
                    </button>
                    <button
                      onClick={() => restoreBackup(backup.name)}
                      className="px-2 py-1 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200"
                    >
                      ♻️ Restore
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default Backups;
